import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/legal';

/**
 * Über mich - Du-Ansprache, Fokus auf Absicherungsberatung (§34d)
 */

const UeberMich = () => {
  const navigate = useNavigate();

  const fadeIn = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  return (
    <div className="min-h-screen bg-navy font-manrope">
      <Navigation variant="dark" />

      {/* HERO with Portrait */}
      <section className="pt-28 sm:pt-36 md:pt-44 pb-12 sm:pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            {/* Text Content - Left */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center md:text-left order-2 md:order-1"
            >
              <p className="text-cobalt text-xs font-medium uppercase tracking-[0.2em] mb-3">
                Über mich
              </p>
              <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-semibold text-white leading-[1.2] tracking-tight mb-4">
                Struktur statt Zufall.
              </h1>
              <p className="text-base sm:text-lg text-white/50 leading-relaxed max-w-lg mb-8 md:mb-12">
                Ein strukturierter Ansatz für Absicherungsentscheidungen, geprägt von 
                Erfahrungen außerhalb der klassischen Finanzwelt.
              </p>
              
              {/* First story block */}
              <div className="space-y-6 text-left">
                <p className="text-lg sm:text-xl text-white/80 leading-relaxed">
                  Ich komme nicht aus der klassischen Finanzberatung.
                </p>
                <p className="text-base text-white/55 leading-[1.8]">
                  Mein Weg in diese Branche war kein geplanter Schritt, sondern hat sich 
                  aus meiner Art zu denken entwickelt. Schon früh haben mich weniger einzelne 
                  Lösungen interessiert, sondern vielmehr die Frage, wie Dinge insgesamt 
                  funktionieren. Warum manche Systeme stabil sind und andere nicht.
                </p>
              </div>
            </motion.div>

            {/* Portrait Image - Right */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex justify-center md:justify-end order-1 md:order-2"
            >
              <div className="w-64 h-80 sm:w-72 sm:h-96 md:w-[22rem] md:h-[32rem] rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl shadow-black/50">
                <img 
                  src="/images/nico-uebermich-new.jpg" 
                  alt="Nico Helfrich"
                  className="w-full h-full object-cover object-[center_top] will-change-auto"
                  loading="eager"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BACKGROUND */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
            <p>
              Dieses Denken hat mich durch meinen bisherigen Weg begleitet.
            </p>
            <p>
              Als Maschinenbauingenieur habe ich in der Entwicklung eines Automobilkonzerns 
              gearbeitet und später in der Produktionsplanung in der Pharmaindustrie, wo es 
              auf Präzision und klare Abläufe ankommt. In meinen Rollen als Projektleiter und 
              stellvertretender Abteilungsleiter im Qualitätsmanagement ging es darum, 
              Verantwortung zu übernehmen und Entscheidungen zu treffen, die nicht nur 
              kurzfristig funktionieren, sondern auch langfristig Bestand haben.
            </p>
            <p>
              Auch die Zeit bei der Bundeswehr als Fallschirmjäger hat mich in dieser Hinsicht 
              geprägt. Dort wird sehr schnell deutlich, dass Entscheidungen klar, durchdacht 
              und belastbar sein müssen. Nicht nur im Moment, sondern auch unter Druck.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CORE STATEMENT */}
      <section className="py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn}>
            <div className="border-l-2 border-cobalt/40 pl-6 sm:pl-8">
              <p className="font-playfair text-xl sm:text-2xl text-white/90 leading-relaxed">
                Es ging nie um einzelne Maßnahmen.
              </p>
              <p className="font-playfair text-xl sm:text-2xl text-white/90 leading-relaxed mt-2">
                Sondern um das Zusammenspiel.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRANSITION TO FINANCE */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
            <p>
              Mit genau diesem Blick habe ich begonnen, mich intensiver mit Absicherung zu 
              beschäftigen, zunächst aus persönlichem Interesse. Ich wollte verstehen, 
              wie finanzielle Entscheidungen tatsächlich wirken, nicht isoliert betrachtet, 
              sondern im Gesamtbild.
            </p>
            <p>
              Dabei ist mir etwas aufgefallen, das ich heute immer wieder sehe: Viele Menschen 
              treffen gute Entscheidungen. Sie sorgen vor und kümmern sich um ihre Absicherung. 
              Und trotzdem passiert all das häufig ohne eine klare Struktur, ohne dass die 
              einzelnen Bausteine wirklich aufeinander abgestimmt sind.
            </p>
            <p className="text-white/70">
              Genau dadurch bleiben oft erhebliche Lücken unbemerkt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* APPROACH - Fokus auf Absicherung */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
            <p className="text-white/70">
              Für mich war das der entscheidende Punkt.
            </p>
            <p>
              Denn sobald man beginnt, Absicherung als System zu betrachten, verändert sich 
              die Herangehensweise grundlegend. Es geht nicht mehr um einzelne Produkte 
              oder isolierte Lösungen, sondern darum, wie alles sinnvoll ineinandergreift. 
              Welche Absicherungen sich ergänzen und welche überflüssig sind.
            </p>
            <p>
              Ein einfaches Beispiel: Eine Berufsunfähigkeitsversicherung macht wenig Sinn, 
              wenn die Absicherungshöhe nicht zum aktuellen Einkommen passt. Gleichzeitig 
              kann ein Krankentagegeld die Lücke in den ersten Wochen schließen, bevor die 
              BU-Rente greift. Und eine Risikolebensversicherung schützt die Familie, falls 
              das Schlimmste passiert.
            </p>
            <p className="text-white/70">
              Entscheidend ist dabei nicht das einzelne Produkt, sondern die Art, 
              wie es in das Gesamtkonzept eingebunden wird.
            </p>
            <p>
              Ich arbeite nicht produktorientiert, sondern strukturiert. Mein Ziel ist es, 
              Zusammenhänge sichtbar zu machen und Lücken zu schließen, bevor sie zum Problem 
              werden. Nicht nur für den Moment, sondern für die Entwicklung über viele Jahre.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn}>
            <p className="text-base text-white/55 leading-[1.8] mb-6">
              Am Ende geht es nicht darum, möglichst viel abzusichern, 
              sondern die richtigen Risiken abzusichern und diese 
              sinnvoll miteinander zu verbinden.
            </p>
            <p className="font-playfair text-lg sm:text-xl text-white/80 leading-relaxed">
              Und genau darin liegt für mich der Unterschied zwischen 
              einzelnen Policen und einer durchdachten Absicherungsstrategie.
            </p>
          </motion.div>
        </div>
      </section>

      {/* POSITIONING & REGULATORY */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn} className="space-y-6 text-base text-white/55 leading-[1.8]">
            <p className="text-white/70">
              Ich arbeite als Versicherungsmakler nach §34d GewO.
            </p>
            <p>
              Die Tätigkeit wird durch die Industrie- und Handelskammer (IHK) reguliert 
              und überwacht. Gleichzeitig unterliegt der Versicherungsmarkt 
              in Deutschland der Aufsicht durch die Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin).
            </p>
            <p className="text-white/70">
              Ich bin an keinen Anbieter gebunden und arbeite ausschließlich im Interesse meiner Kunden.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto px-6 md:px-8">
          <motion.div {...fadeIn}>
            <div className="border-t border-white/10 pt-12 sm:pt-16">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">
                Nächster Schritt
              </p>
              <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-4">
                Lass uns sprechen
              </h2>
              <p className="text-base text-white/50 mb-8 max-w-md">
                Finde heraus, wie deine Absicherung strukturiert aufgebaut werden kann. 
                Ohne Verkaufsdruck, mit klarem Blick auf das Wesentliche.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/strategiegespraech')}
                  className="group px-6 py-3 bg-cobalt text-white rounded-lg text-sm font-medium hover:bg-cobalt/90 transition-colors flex items-center justify-center gap-2"
                  data-testid="ueber-mich-booking-btn"
                >
                  Gespräch vereinbaren
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default UeberMich;
